import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, TextInput } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const errorMessageFrom = (error) => {
  if (!error.response || !error.response.data) return 'Something went wrong. Please try again.';

  const errors = error.response.data;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  if (typeof errors === 'string') return errors;

  return 'Something went wrong. Please try again.';
};

const MetaCapiModal = ({ target, teamId, onClose, onSaved }) => {
  const [accessToken, setAccessToken] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const editingAllVenues = Boolean(target.venueCount);
  const canSubmit = accessToken.trim() !== '';

  const handleSave = async () => {
    if (submitting) return;
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const path = editingAllVenues
        ? `/teams/${teamId}/facebook_pixels/bulk_update`
        : `/teams/${teamId}/facebook_pixels`;
      const payload = { meta_capi: { access_token: accessToken.trim() } };
      if (!editingAllVenues) payload.venue_ownership_id = target.id;

      const { data } = await client.post(path, payload);

      toastSuccess(`Conversions API connected for ${target.name}.`);
      onSaved(data);
      onClose();
    } catch (error) {
      toastError(errorMessageFrom(error));
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;

    onClose();
  };

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
      <Button variant="secondary" disabled={submitting} onClick={handleClose}>Cancel</Button>
      <Button variant="primary" disabled={submitting || !canSubmit} onClick={handleSave}>
        {submitting ? 'Checking token…' : `Connect ${target.name}`}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={handleClose}
      icon={false}
      layout="stacked"
      width={500}
      title="Meta Conversions API"
      footer={footer}
    >
      <div style={{ textAlign: 'left' }}>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: 'var(--sl-text-secondary, #414651)',
            lineHeight: 1.5,
          }}
        >
          {editingAllVenues ? (
            <>
              This token will be used for <strong>all {target.venueCount} venues</strong>, each
              sending to its own Facebook pixel ID. It must have access to every one of those
              pixels, or nothing is saved.
            </>
          ) : (
            <>
              Sends purchases to Meta from our servers as well as the browser, so sales are still
              attributed when a fan blocks the pixel. Events go to{' '}
              <strong>{target.name}</strong>&apos;s Facebook pixel ID.
            </>
          )}
        </p>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: 'var(--sl-text-quaternary, #717680)',
            lineHeight: 1.5,
          }}
        >
          In Meta Events Manager, open your dataset, then Settings → Conversions API → Generate
          access token. We send a test event to check the token before saving it.
        </p>
        <TextInput
          label="Access token"
          type="password"
          autoComplete="off"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder="EAAG..."
          autoFocus
        />
      </div>
    </Modal>
  );
};

MetaCapiModal.propTypes = {
  target: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string,
    venueCount: PropTypes.number,
  }).isRequired,
  teamId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default MetaCapiModal;
